import { DataTable } from "@/components/common/DataTable";
import StaffingModal from "./StaffingModal";
import { useApiClient } from "@/context/ApiContext";
import { getApplicantsWithFilters } from "@/services/applicant/Applicant";
import { getUsersWithFilters } from "@/services/user/User";
import { getProcessesForPosting, reopenProcess, ReopenProcessRes } from "@/services/staffing/Staffing";
import { Applicant } from "@/types/common/applicant";
import { StaffMember } from "@/types/common/staffMember";
import { Process, ProcessRow } from "@/types/staffing/process";
import { ODataParams } from "@/utils/odataUtil";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getProcessModalColumns } from "../columns/StaffingDashboardColumns";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { describeReopenChatError, openStaffingGroupByReferenceId, PROCESS_STATUS_REOPENED, ReopenChatError } from "@/utils/staffingUtil";
interface InProcessStaffingProps {
    processes: Process[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
    clientId: number | null;
    clientName: string | null;
    postingId: number | null;
    /** Called after a chat was reopened (task 13574) so the worksheet's process count can refresh. */
    onProcessReopened?: () => void;
}



const InProcessStaffing = ({ processes, open, onOpenChange, clientId, clientName, postingId, onProcessReopened }: InProcessStaffingProps) => {
    const [rows, setRows] = useState<ProcessRow[]>(processes);
    const [processList, setProcessList] = useState<Process[]>(processes);
    const [postingStatus, setPostingStatus] = useState<string | null>(null);
    const [reopeningId, setReopeningId] = useState<number | null>(null);
    const [reopenError, setReopenError] = useState<ReopenChatError | null>(null);
    const apiClient = useApiClient();

    /**
     * "Re-open chat" (task 13574): POST staffing/process/{id}/reopen — PBS Live rehydrates the chat
     * (+7 days), the row becomes `Reopened`, then the chat opens in the host widget. On failure
     * the row is untouched (the API only writes after PBS Live succeeded), so the click can be
     * retried; the error is shown above the table.
     */
    const reopenChat = useCallback((processId: number, referenceId: string) => {
        setReopenError(null);
        setReopeningId(processId);
        reopenProcess(processId, apiClient)
            .then((res) => {
                const result: ReopenProcessRes | undefined = res.data;
                const status = result?.status || PROCESS_STATUS_REOPENED;
                const stamp = (list: ProcessRow[]) => list.map(r => r.Id === processId ? { ...r, Status: status } : r);
                setRows(stamp);
                setProcessList(stamp);
                onProcessReopened?.();
                openStaffingGroupByReferenceId(result?.referenceId || referenceId);
            })
            .catch((err) => {
                console.error(err);
                setReopenError(describeReopenChatError(err));
            })
            .finally(() => setReopeningId(null));
    }, [apiClient, onProcessReopened]);

    const processModalColumns = useMemo(
        () => getProcessModalColumns(clientId ?? 0, { postingStatus, onReopenChat: reopenChat, reopeningProcessId: reopeningId }),
        [clientId, postingStatus, reopenChat, reopeningId]
    );
    const getStaffPromise = useCallback((staff: ProcessRow): Promise<ProcessRow> => {
        const login : string = staff.Staff.split('\\')[1];
        const filters: ODataParams = {
            filter: `LoginName eq '${login}'`
        };
        return getUsersWithFilters(filters, apiClient)
            .then((res) => {
                const user: StaffMember = res.data.value[0];
                return user ? { ...staff, City: user.City, FullName: user.FullName, StaffId: user.Id, Role: user.Role } : staff;
            })
            .catch(err => {
                console.error(err);
                return staff;
            });
    }, [apiClient]);
    const getApplicantPromise = useCallback((applicant: ProcessRow): Promise<ProcessRow> => {
        const filters: ODataParams = {
            filter: `Email eq '${applicant.Staff}'`
        };
        return getApplicantsWithFilters(filters, apiClient)
            .then((res) => {
                const user: Applicant = res.data.value[0];
                return user ? { ...applicant, City: user.City, FullName: user.FullName, StaffId: user.Id, Role: user.Role } : applicant;
            })
            .catch(err => {
                console.error(err);
                return applicant;
            });
    }, [apiClient]);
    useEffect(() => {
        setProcessList(processes);
    }, [processes])
    useEffect(() => {
        if (!open || postingId == null) return;
        setPostingStatus(null);
        setReopenError(null);
        getProcessesForPosting(postingId, apiClient)
            .then((res) => {
                const posting = res.data.value?.[0];
                if (posting?.Status) setPostingStatus(posting.Status);
                if (posting?.Processes) setProcessList(posting.Processes);
            })
            .catch(err => console.error(err))
    }, [open, postingId, apiClient])
    useEffect(() => {
        const staff: ProcessRow[] = processList.filter((x) => !x.Applicant)
        const applicants: ProcessRow[] = processList.filter((x) => x.Applicant)
        let promises: Promise<ProcessRow>[] = [];
        staff.forEach((row) => {
            promises.push(getStaffPromise(row));
        })
        applicants.forEach((row) => {
            promises.push(getApplicantPromise(row));
        })
        Promise.all(promises)
            .then((res) => {
                setRows(res);
            })
    }, [processList, getStaffPromise, getApplicantPromise])
    return (
        <StaffingModal open={open} onOpenChange={onOpenChange} title={"Staffing in Process" + (clientName ? ' - ' + clientName : '')} wide>
            <div className="flex flex-col gap-2">
                {reopenError && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>{reopenError.title}</AlertTitle>
                        <AlertDescription>{reopenError.detail}</AlertDescription>
                    </Alert>
                )}
                <DataTable data={rows} columns={processModalColumns} />
            </div>
        </StaffingModal>
    );
};

export default InProcessStaffing;
